import {
  injectWiggleAnimation,
  styles as sharedStyles,
} from "../../Shared/shared.styles";
import { styles } from "./extensionDetails.styles";
import { useParams } from "react-router-dom";
import {
  useFetchBaseJsonConfig,
  useFetchExtensionConfigOpenPrs,
} from "../../Hooks/AdoHooks/UseFetchBaseJsonConfig";
import ExtensionDetailsForm from "./ExtensionDetailsForm/ExtensionDetailsForm";
import { useBoolean } from "@fluentui/react-hooks";
import isEqual from "lodash/isEqual";
import {
  CommandBar,
  Icon,
  Link,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Stack,
  Text,
} from "@fluentui/react";
import ExperimentBreadcrumbs from "../../Shared/ExperimentBreadcrumbs";
import ExtensionDetailsOverview from "./ExtensionDetailsOverview";
import ExtensionDetailsConfirmationModel from "./ExtensionDetailsConfirmationModel/ExtensionDetailsConfirmationModel";
import { useEffect, useMemo, useState } from "react";
import { ExtensionDetailsType } from "../../Types";
import { useToast } from "../../Contexts/ToastContext";
import { DEFAULT_EXTENSION_CONFIG, REPO_URL } from "../../Shared/shared.consts";
import { useEv2ExperimentParams } from "../../Hooks/UseCurrentExtension";
import useAppInsights from "../../Hooks/UseAppInsights";
import { NavType } from "../../Shared/AppInsights";
import { useDataAccessContext } from "../../Contexts/DataAccessContext";

/**
 * Component for viewing and editing extension details configuration (base.json).
 * Allows users to edit the extension details in a form, and upon saving changes, creates or updates a pull request in Azure DevOps with the updated extension details configuration file.
 */
const ExtensionDetails: React.FC = () => {
  const { showToast } = useToast();
  const { hasAdoAccess } = useDataAccessContext();
  const [formState, setFormState] = useState<ExtensionDetailsType>(
    DEFAULT_EXTENSION_CONFIG,
  );
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({});
  const [saveWiggle, setSaveWiggle] = useState(false);
  const { extensionName } = useParams<{ extensionName: string }>();
  const { extensionFolder } = useEv2ExperimentParams();
  const { trackNavigationEvent, trackOperationEvent } =
    useAppInsights("ExtensionDetails");
  const [isEdit, { setTrue: enableEditMode, setFalse: disableEditMode }] =
    useBoolean(false);
  const [
    confirmationModelOpen,
    { setTrue: openConfirmationModel, setFalse: closeConfirmationModel },
  ] = useBoolean(false);

  const { value: extensionDetails, loading } = useFetchBaseJsonConfig();
  const {
    value: configOpenPr,
    loading: configOpenPrLoading,
    refresh,
  } = useFetchExtensionConfigOpenPrs();

  const initialExtensionConfig = useMemo(
    () => configOpenPr?.extensionDetails ?? extensionDetails,
    [configOpenPr, extensionDetails],
  );

  const configsLoading = useMemo(
    () => loading || configOpenPrLoading,
    [loading, configOpenPrLoading],
  );

  const extensionFormHasErrors = useMemo(
    () => Object.keys(formErrors).length > 0,
    [formErrors],
  );

  const formChanged = useMemo(() => {
    return !isEqual(formState, initialExtensionConfig);
  }, [formState, initialExtensionConfig]);

  const handleSave = () => {
    if (extensionFormHasErrors) {
      setSaveWiggle(true);
      setTimeout(() => setSaveWiggle(false), 1000);
      return;
    }
    openConfirmationModel();
    trackOperationEvent({
      operation: "Save",
      function: "handleSave",
      properties: { formState },
    });
  };

  const handleEdit = () => {
    enableEditMode();
    trackOperationEvent({
      operation: "Edit",
      function: "handleEdit",
    });
  };

  const handleCancel = () => {
    if (initialExtensionConfig) {
      setFormState(initialExtensionConfig);
    }
    disableEditMode();
    trackOperationEvent({
      operation: "Cancel",
      function: "handleCancel",
      properties: { formState },
    });
  };

  const onSaveSuccess = () => {
    showToast({
      message: `Extension configuration pull request successfully ${configOpenPr?.prUrl ? "updated" : "created"} for extension "${extensionName}"`,
      type: "success",
      duration: 5000,
      dismissible: true,
    });
    refresh();
    disableEditMode();
    closeConfirmationModel();
  };

  useEffect(() => {
    // Set form state to initial extension configs once configs load
    // We have to check for a config from a PR and from the existing config
    if (!configsLoading && initialExtensionConfig) {
      setFormState(initialExtensionConfig);
    }
  }, [configsLoading, initialExtensionConfig]);

  if (configsLoading) {
    return (
      <Spinner
        label="Loading extension details..."
        size={SpinnerSize.large}
        className={sharedStyles.loadingSpinner}
      />
    );
  }

  return (
    <Stack className={sharedStyles.pageStyles}>
      <ExperimentBreadcrumbs
        isClone={false}
        isCreate={false}
        secondBreadcrumb="Extension details"
      />
      <Stack tokens={{ childrenGap: 10 }} className={styles.extensionDetails}>
        {!initialExtensionConfig && (
          <MessageBar messageBarType={MessageBarType.severeWarning}>
            No extension configuration found. If no <em>base.json</em> is found
            at the
            <Link
              href={`${REPO_URL}?path=/Extensions/${extensionFolder?.extensionRepoPath}`}
              target="_blank"
              onClick={() => {
                trackNavigationEvent({
                  destination: `${REPO_URL}?path=/Extensions/${extensionFolder?.extensionRepoPath}`,
                  navType: NavType.External,
                });
              }}
            >
              Repository configurations
            </Link>{" "}
            root, please fill out and submit the Extension Configuration form as
            soon as possible.
          </MessageBar>
        )}
        <ExtensionDetailsOverview
          overviewMessage={
            configOpenPr && (
              <MessageBar messageBarType={MessageBarType.warning}>
                There is an open pull request to update extension
                configurations. Ensure you have read all{" "}
                <Link
                  href="https://eng.ms/docs/products/azure-portal-framework-ibizafx/experimentation/ev2configurationspec#configuration-inheritance"
                  target="_blank"
                  onClick={() => {
                    trackNavigationEvent({
                      destination:
                        "https://eng.ms/docs/products/azure-portal-framework-ibizafx/experimentation/ev2configurationspec#configuration-inheritance",
                      navType: NavType.External,
                    });
                  }}
                >
                  Documentation
                </Link>{" "}
                on configuration inheritance before merging updates as changes
                to extension configurations can impact all experiments under the
                extension.
                <Link
                  href={configOpenPr.prUrl}
                  target="_blank"
                  onClick={() => {
                    trackNavigationEvent({
                      destination: configOpenPr.prUrl,
                      navType: NavType.External,
                    });
                  }}
                >
                  Review pull request <Icon iconName="NavigateExternalInline" />
                </Link>
              </MessageBar>
            )
          }
        />
        <Stack className={styles.extensionConfiguration}>
          <Stack className={styles.extensionConfigurationHeader}>
            <Stack
              horizontal
              className={styles.extensionConfigurationHeaderText}
            >
              <Text variant="large">Extension configuration</Text>
            </Stack>
            <CommandBar
              className={styles.extensionConfigurationCommandBar}
              items={[
                {
                  key: "edit",
                  text: "Edit details",
                  iconProps: {
                    iconName: "Edit",
                    style: { fontSize: "12px" },
                  },
                  disabled: isEdit || !formState || !hasAdoAccess,
                  onClick: handleEdit,
                },
                {
                  key: "save",
                  text: "Save details",
                  iconProps: {
                    iconName: "Save",
                    style: { fontSize: "12px" },
                  },
                  title: !formChanged
                    ? "No changes to save"
                    : extensionFormHasErrors
                      ? "Please fix form errors before saving"
                      : "Save details",
                  onClick: handleSave,
                  buttonStyles: {
                    root: saveWiggle ? injectWiggleAnimation : undefined,
                  },
                  disabled: !formChanged || !isEdit || !hasAdoAccess,
                },
                {
                  key: "cancel",
                  text: "Cancel edit",
                  iconProps: {
                    iconName: "Cancel",
                    style: { fontSize: "12px" },
                  },
                  onClick: handleCancel,
                  disabled: !isEdit,
                },
              ]}
            />
          </Stack>
          <Stack className={styles.extensionConfigurationForm}>
            <MessageBar messageBarType={MessageBarType.info}>
              Extension configurations are shared across all experiments. Please
              apply caution when making changes to these configurations as
              changes made here will impact all experiments under this
              extension. Please read
              <Link
                href="https://eng.ms/docs/products/azure-portal-framework-ibizafx/experimentation/ev2configurationspec#configuration-inheritance"
                target="_blank"
                onClick={() => {
                  trackNavigationEvent({
                    destination:
                      "https://eng.ms/docs/products/azure-portal-framework-ibizafx/experimentation/ev2configurationspec#configuration-inheritance",
                    navType: NavType.External,
                  });
                }}
              >
                Configuration inheritance
              </Link>{" "}
              for more information on base configurations.
            </MessageBar>
            <ExtensionDetailsForm
              formState={formState}
              setFormState={setFormState}
              isEditMode={isEdit}
              errors={formErrors}
              setErrors={setFormErrors}
            />
          </Stack>
        </Stack>
      </Stack>
      {confirmationModelOpen && formState && (
        <ExtensionDetailsConfirmationModel
          extensionDetails={formState}
          isCreate={!initialExtensionConfig}
          prUrl={configOpenPr?.prUrl}
          onCancel={closeConfirmationModel}
          onSaveSuccess={onSaveSuccess}
        />
      )}
    </Stack>
  );
};

export default ExtensionDetails;
